import './assets/main.css';
import 'primeicons/primeicons.css';

import { createApp } from 'vue';
import ToastService from 'primevue/toastservice';
import ConfirmationService from 'primevue/confirmationservice';
import PrimeVue from 'primevue/config';
import { createI18n } from 'vue-i18n';
import { createPinia } from 'pinia';
import App from './App.vue';
import router from './router';
import { useAppStore } from './stores/appStore';
import { FeatureFlagsService } from '@erebus/feature-flags';
import { erebus } from '@/utilities/axios';
import { FEATURE_FLAGS_SERVICE_KEY } from '@/services/injection-keys';
import { initFeatureFlagsService } from '@/utilities/featureFlagsService';
import { PRIMEVUE_Z_INDEX } from '@/utilities/primeVueLayers';
import { createPreset } from '@erebus/theme';
import { getRuntimeConfig } from '@erebus/runtime-config';
import { mergeUiMessages } from '@erebus/ui/i18n';

import i18nData from './i18n.json';
import Tooltip from 'primevue/tooltip';

// mergeUiMessages layers @erebus/ui's own `ui.*` text underneath ours, so the
// shared components have wording without console restating it. Our keys win,
// so any shared string can be reworded here.
const i18n = createI18n({
  locale: 'en',
  messages: mergeUiMessages(i18nData),
});

const app = createApp(App);
app.use(PrimeVue, {
  zIndex: PRIMEVUE_Z_INDEX,
  theme: {
    preset: createPreset(getRuntimeConfig().branding),
    options: {
      darkModeSelector: '.dark-mode',
      cssLayer: {
        name: 'primevue',
        order: 'theme, base, primevue, components, utilities',
      },
    },
  },
});
app.use(ToastService);
app.use(ConfirmationService);

// Initialize Pinia
app.use(createPinia());

// Provide services for dependency injection
const featureFlagsService = new FeatureFlagsService(erebus);
app.provide(FEATURE_FLAGS_SERVICE_KEY, featureFlagsService);

// Initialize feature flags service singleton (for use in stores where inject() isn't available)
initFeatureFlagsService(erebus);

// Bootstrap auth before installing the router so navigation guards
// see the correct auth state on the initial route evaluation.
//
// The catch is a safety net, not the real error handling: bootstrap already
// handles its own failures. Anything that still escapes must not stop
// app.mount() below, or the user gets a blank page (PRX-2066).
const appStore = useAppStore();
try {
  await appStore.bootstrap();
} catch (err) {
  console.error('Auth bootstrap threw unexpectedly, mounting anyway:', err);
}

app.use(router);
app.use(i18n);
app.directive('tooltip', Tooltip);

app.mount('#app');

export default app;
